import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as portalOrganizationsApi from '@/api/portalOrganizations'
import type { PortalOrganization } from '@/types/portal'

const ACTIVE_ORG_KEY = 'portal.activeOrganizationId'

function readStoredId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY)
  } catch {
    return null
  }
}

function persistId(id: string | null) {
  try {
    if (id) localStorage.setItem(ACTIVE_ORG_KEY, id)
    else localStorage.removeItem(ACTIVE_ORG_KEY)
  } catch {
    return
  }
}

export const usePortalOrganizationsStore = defineStore('portalOrganizations', () => {
  const items = ref<PortalOrganization[]>([])
  const activeId = ref<string | null>(readStoredId())
  const loading = ref(false)

  const isMultiOrg = computed(() => items.value.length > 1)

  const active = computed(() =>
    items.value.find((o) => o.id === activeId.value) ?? null,
  )

  function setActive(id: string | null) {
    activeId.value = id
    persistId(id)
  }

  function adoptFromContract(organizationId: string | null | undefined) {
    if (!organizationId || organizationId === activeId.value) return
    setActive(organizationId)
  }

  async function fetchAll() {
    if (loading.value) return
    loading.value = true
    try {
      const { data } = await portalOrganizationsApi.list()
      items.value = data
      const stillAccessible = data.some((o) => o.id === activeId.value)
      if (!stillAccessible) setActive(data[0]?.id ?? null)
    } catch {
      items.value = []
    } finally {
      loading.value = false
    }
  }

  function reset() {
    items.value = []
    setActive(null)
  }

  return { items, activeId, active, loading, isMultiOrg, fetchAll, setActive, adoptFromContract, reset }
})

export function activeOrganizationId(): string | null {
  return readStoredId()
}
